import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  ClanTreasury,
  createClanProposal,
  depositClanTreasury,
  getClanTreasury,
  treasuryErrorMessage,
  voteClanProposal,
} from '../../../api/payment/clan-treasury.api';
import { ClanMember } from '../../../api/group/clan.api';
import { fetchMemberProfile } from '../../../api/group/arena.api';
import { parseArenaDate } from '../../arena/arena-format';

interface Props {
  members: ClanMember[];
  myUserId: string;
  /** Chủ hoặc phó clan: được tạo đề xuất chi và bỏ phiếu. */
  canManage: boolean;
}

const BTN = 'rounded-full px-4 py-2 text-[14px] font-medium transition disabled:opacity-60';
const fmt = (n: number) => Number(n || 0).toLocaleString('vi-VN');
// Payment trả giờ UTC không kèm hậu tố múi giờ; hiển thị theo giờ Việt Nam.
const fmtDate = (s?: string | null) =>
  s ? parseArenaDate(s).toLocaleDateString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }) : '';
const TX_LABEL: Record<string, string> = {
  DEPOSIT: 'Góp quỹ',
  ARENA_REWARD: 'Thưởng thử thách',
  PAYOUT: 'Chi cho thành viên',
};
const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Đang chờ duyệt',
  APPROVED: 'Đã duyệt',
  REJECTED: 'Đã từ chối',
  EXPIRED: 'Hết hạn',
};
const shortName = (id: string) => `Thành viên …${(id || '').slice(-4)}`;

const ClanTreasurySection: React.FC<Props> = ({ members, myUserId, canManage }) => {
  const [data, setData] = useState<ClanTreasury | null>(null);
  const [busy, setBusy] = useState(false);
  const [deposit, setDeposit] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [toUserId, setToUserId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const [names, setNames] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      setData(await getClanTreasury());
    } catch (e: any) {
      toast.error(treasuryErrorMessage(e, 'Không tải được quỹ clan'));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Tên thật của thành viên và người liên quan đến đề xuất/sổ quỹ, tải một lần cho mỗi người.
  useEffect(() => {
    const ids = new Set<string>(members.map((m) => m.userId));
    data?.proposals.forEach((p) => {
      ids.add(p.toUserId);
      ids.add(p.createdBy);
    });
    data?.transactions.forEach((t) => t.userId && ids.add(t.userId));
    const todo = Array.from(ids).filter((id) => id && !(id in names));
    if (todo.length === 0) return;
    Promise.all(
      todo.map((id) =>
        fetchMemberProfile(id)
          .then((p) => [id, p.fullName || ''] as const)
          .catch(() => [id, ''] as const)
      )
    ).then((pairs) => setNames((prev) => ({ ...prev, ...Object.fromEntries(pairs) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, data]);

  const act = async (fn: () => Promise<unknown>, okMsg: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      await load();
    } catch (e: any) {
      toast.error(treasuryErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const nameOf = (id: string) => names[id] || shortName(id);
  // Không mời chính mình: backend từ chối tự chi cho mình.
  const recipients = members.filter((m) => m.userId !== myUserId);
  const pending = data.proposals.filter((p) => p.status === 'PENDING');
  const decided = data.proposals.filter((p) => p.status !== 'PENDING');

  const submitDeposit = () => {
    const n = Math.floor(Number(deposit));
    if (!n || n <= 0) return toast.error('Nhập số Ca đào muốn góp.');
    act(async () => {
      await depositClanTreasury(n);
      setDeposit('');
    }, 'Đã góp vào quỹ clan');
  };

  const submitProposal = () => {
    const n = Math.floor(Number(amount));
    if (!toUserId) return toast.error('Chọn người nhận.');
    if (!n || n <= 0) return toast.error('Nhập số Ca đào cần chi.');
    if (!reason.trim()) return toast.error('Nhập lý do chi.');
    act(async () => {
      await createClanProposal(toUserId, n, reason.trim());
      setShowForm(false);
      setToUserId('');
      setAmount('');
      setReason('');
    }, 'Đã tạo đề xuất chi');
  };

  return (
    <section className="flex flex-col gap-3" data-testid="clan-treasury">
      <h2 className="text-[20px] font-semibold text-[#19052f] dark:text-white">Quỹ clan</h2>
      <div className="flex flex-col gap-3 rounded-[16px] bg-white p-4 shadow-[4px_4px_44px_rgba(0,0,0,0.05)] dark:bg-slate-800">
        <div>
          <div className="text-[13px] text-[#657384] dark:text-slate-400">Số dư quỹ</div>
          <div className="text-[26px] font-bold text-[#19052f] dark:text-white" data-testid="treasury-balance">
            {fmt(data.balance)} Ca đào
          </div>
          <p className="text-[12px] text-[#657384] dark:text-slate-400">Quỹ chỉ giữ Ca đào, không đổi ra tiền.</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input
            type="number"
            min={1}
            aria-label="Số Ca đào góp"
            placeholder="Số Ca đào"
            value={deposit}
            onChange={(e) => setDeposit(e.target.value)}
            className="w-40 rounded-full border border-[#DEE0E6] px-4 py-2 text-[14px] dark:border-slate-600 dark:bg-slate-900 dark:text-white"
          />
          <button className={`${BTN} bg-[#066bff] text-white`} disabled={busy} onClick={submitDeposit}>
            Góp Ca đào
          </button>
          {canManage && (
            <button
              className={`${BTN} border border-[#DEE0E6] text-[#19052f] dark:border-slate-600 dark:text-white`}
              onClick={() => setShowForm((v) => !v)}
            >
              Tạo đề xuất chi
            </button>
          )}
        </div>

        {canManage && showForm && (
          <div className="flex flex-col gap-2 rounded-[12px] border border-[#DEE0E6] p-3 dark:border-slate-600">
            <select
              aria-label="Người nhận"
              value={toUserId}
              onChange={(e) => setToUserId(e.target.value)}
              className="rounded-[10px] border border-[#DEE0E6] px-3 py-2 text-[14px] dark:border-slate-600 dark:bg-slate-900 dark:text-white"
            >
              <option value="">Chọn người nhận</option>
              {recipients.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {nameOf(m.userId)}
                </option>
              ))}
            </select>
            <input
              type="number"
              min={1}
              aria-label="Số Ca đào chi"
              placeholder="Số Ca đào chi"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="rounded-[10px] border border-[#DEE0E6] px-3 py-2 text-[14px] dark:border-slate-600 dark:bg-slate-900 dark:text-white"
            />
            <input
              aria-label="Lý do chi"
              placeholder="Lý do chi"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="rounded-[10px] border border-[#DEE0E6] px-3 py-2 text-[14px] dark:border-slate-600 dark:bg-slate-900 dark:text-white"
            />
            <button className={`${BTN} w-fit bg-[#066bff] text-white`} disabled={busy} onClick={submitProposal}>
              Gửi đề xuất
            </button>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <h3 className="text-[15px] font-semibold text-[#19052f] dark:text-white">Đề xuất chi</h3>
          {data.proposals.length === 0 && <p className="text-[13px] text-[#657384]">Chưa có đề xuất nào.</p>}
          {pending.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-[12px] border border-[#EAEFFA] p-3 dark:border-slate-700">
              <div className="text-[14px] text-[#19052f] dark:text-white">
                <b>{fmt(p.amount)} Ca đào</b> cho {p.toUserId === myUserId ? 'bạn' : nameOf(p.toUserId)}
                <div className="text-[12px] text-[#657384]">
                  {p.reason} · {p.approveCount} đồng ý, {p.rejectCount} từ chối
                </div>
              </div>
              {canManage && p.myVote == null && (
                <div className="flex gap-2">
                  <button className={`${BTN} bg-[#066bff] text-white`} disabled={busy} onClick={() => act(() => voteClanProposal(p.id, true), 'Đã đồng ý')}>
                    Đồng ý
                  </button>
                  <button className={`${BTN} border border-[#E11D48] text-[#E11D48]`} disabled={busy} onClick={() => act(() => voteClanProposal(p.id, false), 'Đã từ chối')}>
                    Từ chối
                  </button>
                </div>
              )}
              {canManage && p.myVote != null && (
                <span className="text-[13px] text-[#657384]">Bạn đã {p.myVote ? 'đồng ý' : 'từ chối'}</span>
              )}
            </div>
          ))}
          {decided.map((p) => (
            <div key={p.id} className="text-[13px] text-[#657384] dark:text-slate-400">
              {fmt(p.amount)} Ca đào cho {nameOf(p.toUserId)}: {STATUS_LABEL[p.status] || p.status}
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-1">
          <h3 className="text-[15px] font-semibold text-[#19052f] dark:text-white">Sổ quỹ</h3>
          {data.transactions.length === 0 && <p className="text-[13px] text-[#657384]">Chưa có giao dịch.</p>}
          {data.transactions.map((t) => (
            <div key={t.id} className="flex items-center justify-between text-[13px] text-[#3b4351] dark:text-slate-300">
              <span>
                {TX_LABEL[t.type] || t.type}
                {t.userId ? ` · ${nameOf(t.userId)}` : ''} · {fmtDate(t.createdAt)}
              </span>
              <span className={t.type === 'PAYOUT' ? 'text-[#E11D48]' : 'text-emerald-600'}>
                {t.type === 'PAYOUT' ? '-' : '+'}
                {fmt(t.amount)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ClanTreasurySection;
